/**
 * CommandPaletteModal.tsx
 *
 * WCAG 2.2 AA compliant dialog-based Command Palette & Universal Help Search (`Cmd+K` / `Ctrl+K`).
 *
 * Standards implemented:
 * 1. `role="dialog"`, `aria-modal="true"`, `aria-labelledby="command-palette-title"`.
 * 2. Focus Restoration Anchor: Captures active element on open and restores focus on close
 *    (falls back to `#main-content`).
 * 3. Listbox pattern: `role="listbox"` / `role="option"` with `aria-selected` and `aria-activedescendant`.
 * 4. Categorized results (Actions, Navigation, Help, Entities).
 * 5. Runtime authorization verification before command execution.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { globalAccessibilityManager } from '../../a11y/accessibility-manager.js';

import { isPluginIdEnabled } from '../channels/channel-plugin-registry.js';

export type CommandCategory = 'action' | 'navigation' | 'help' | 'entity';

export interface CommandDefinition {
  readonly id: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly category: CommandCategory;
  readonly shortcut?: string;
  readonly pluginId?: string;
  readonly requiredPermissions?: readonly string[];
  readonly requiresConfirmation?: boolean;
  readonly execute: () => Promise<void> | void;
}

export interface CommandPaletteModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly commands?: readonly CommandDefinition[];
  readonly userPermissions?: readonly string[];
  readonly activeRoute?: string;
  readonly enabledPlugins?: Record<string, boolean>;
}

const DEFAULT_COMMANDS: CommandDefinition[] = [
  {
    id: 'nav-scrum',
    title: 'Go to Engineering (#scrum)',
    subtitle: 'Sprint backlogs, daily standups, and Kanban tasks',
    category: 'navigation',
    shortcut: 'G S',
    pluginId: 'ScrumMasterPlugin',
    execute: () => {
      window.dispatchEvent(new CustomEvent('msng3r:navigate', { detail: { route: 'scrum' } }));
    },
  },
  {
    id: 'nav-crm',
    title: 'Go to Sales Pipeline (#crm)',
    subtitle: 'Leads, deals, customer accounts, and revenue pipelines',
    category: 'navigation',
    shortcut: 'G C',
    pluginId: 'CrmPlugin',
    execute: () => {
      window.dispatchEvent(new CustomEvent('msng3r:navigate', { detail: { route: 'crm' } }));
    },
  },
  {
    id: 'nav-wiki',
    title: 'Go to Knowledge Wiki (#wiki)',
    subtitle: 'Engineering runbooks, architecture specs, and documentation',
    category: 'navigation',
    shortcut: 'G W',
    pluginId: 'LLMWikiPlugin',
    execute: () => {
      window.dispatchEvent(new CustomEvent('msng3r:navigate', { detail: { route: 'wiki' } }));
    },
  },
  {
    id: 'act-new-channel',
    title: 'Create New Channel / Private Group',
    subtitle: 'Start a WhatsApp-style private group or community channel',
    category: 'action',
    shortcut: 'C C',
    execute: () => {
      window.dispatchEvent(new CustomEvent('msng3r:open-modal', { detail: { modal: 'create-channel' } }));
    },
  },
  {
    id: 'act-import-kanban',
    title: 'Import Kanban Board (JSON/CSV)',
    subtitle: 'Upload and restore tasks with conflict preview',
    category: 'action',
    execute: () => {
      window.dispatchEvent(new CustomEvent('msng3r:open-modal', { detail: { modal: 'import-kanban' } }));
    },
  },
  {
    id: 'help-scrum-blockers',
    title: 'Help: How to Unblock Tickets & Runbooks',
    subtitle: 'Scrum copilot guide to investigating blocker cascades',
    category: 'help',
    execute: () => {
      window.dispatchEvent(new CustomEvent('msng3r:open-help', { detail: { topic: 'scrum-blockers' } }));
    },
  },
  {
    id: 'help-crm-pipeline',
    title: 'Help: Sales Stage Transition Rules',
    subtitle: 'Understanding probability, deal gates, and closed-won policies',
    category: 'help',
    execute: () => {
      window.dispatchEvent(new CustomEvent('msng3r:open-help', { detail: { topic: 'crm-pipeline' } }));
    },
  },
  {
    id: 'help-shortcuts',
    title: 'Help: Global Keyboard Shortcuts Cheat Sheet',
    subtitle: 'List of all universal hotkeys and landmark cycling commands',
    category: 'help',
    shortcut: '?',
    execute: () => {
      window.dispatchEvent(new CustomEvent('msng3r:open-help', { detail: { topic: 'shortcuts' } }));
    },
  },
];

export const CommandPaletteModal: React.FC<CommandPaletteModalProps> = ({
  isOpen,
  onClose,
  commands = DEFAULT_COMMANDS,
  userPermissions = ['*'],
  activeRoute: _activeRoute,
  enabledPlugins,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  // Focus management: capture active element on open, restore on close
  useEffect(() => {
    if (isOpen) {
      globalAccessibilityManager.captureActiveFocus();
      setSearchQuery('');
      setSelectedIndex(0);

      // Focus input on next frame
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      globalAccessibilityManager.announce('Command Palette opened. Type to filter actions.', 'polite');
    } else {
      globalAccessibilityManager.restoreActiveFocus('#main-content');
    }
  }, [isOpen]);

  // Filter commands by query, user permissions, and enabled plugins
  const filteredCommands = React.useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    return commands.filter((cmd) => {
      // Plugin status check
      if (cmd.pluginId && !isPluginIdEnabled(enabledPlugins, cmd.pluginId)) {
        return false;
      }

      // Permission check
      if (cmd.requiredPermissions && cmd.requiredPermissions.length > 0) {
        const hasPermission = cmd.requiredPermissions.every(
          (p) => userPermissions.includes('*') || userPermissions.includes(p)
        );
        if (!hasPermission) return false;
      }

      if (!q) return true;
      return (
        cmd.title.toLowerCase().includes(q) ||
        (cmd.subtitle && cmd.subtitle.toLowerCase().includes(q)) ||
        cmd.category.toLowerCase().includes(q)
      );
    });
  }, [commands, searchQuery, userPermissions, enabledPlugins]);

  // Adjust selection when filtered results change
  useEffect(() => {
    if (selectedIndex >= filteredCommands.length) {
      setSelectedIndex(Math.max(0, filteredCommands.length - 1));
    }
  }, [filteredCommands.length, selectedIndex]);

  // Keyboard navigation & Focus trapping
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredCommands.length));
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % Math.max(1, filteredCommands.length));
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const selected = filteredCommands[selectedIndex];
        if (selected) {
          executeCommand(selected);
        }
        return;
      }

      if (e.key === 'Tab' && modalRef.current) {
        globalAccessibilityManager.trapFocus(modalRef.current, e.nativeEvent);
      }
    },
    [filteredCommands, selectedIndex, onClose]
  );

  const executeCommand = async (cmd: CommandDefinition) => {
    // Runtime permission verification
    if (cmd.requiredPermissions && cmd.requiredPermissions.length > 0) {
      const hasPermission = cmd.requiredPermissions.every(
        (p) => userPermissions.includes('*') || userPermissions.includes(p)
      );
      if (!hasPermission) {
        globalAccessibilityManager.announce('Permission denied for this command.', 'assertive');
        return;
      }
    }

    onClose();
    try {
      await cmd.execute();
      globalAccessibilityManager.announce(`Executed ${cmd.title}`, 'polite');
    } catch (err) {
      globalAccessibilityManager.announce(`Failed to execute ${cmd.title}`, 'assertive');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="command-palette-backdrop fixed inset-0 z-50 flex items-start justify-center pt-20"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '80px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="command-palette-title"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        style={{
          width: '640px',
          maxWidth: '92vw',
          maxHeight: '75vh',
          backgroundColor: 'var(--theme-bg-modal, #0f172a)',
          border: '1px solid var(--theme-border-highlight, #38bdf8)',
          borderRadius: 'var(--theme-radius-xl, 16px)',
          boxShadow: 'var(--theme-shadow-lg, 0 20px 40px rgba(0,0,0,0.6)), var(--theme-shadow-glow)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header with accessible title */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--theme-border, #1e293b)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span style={{ fontSize: '18px', color: 'var(--theme-accent, #38bdf8)' }}>⚡</span>
          <h2
            id="command-palette-title"
            style={{
              fontSize: 'var(--theme-text-base, 14px)',
              fontWeight: 600,
              color: 'var(--theme-text-primary, #f8fafc)',
              flex: 1,
            }}
          >
            Universal Command & Help Palette
          </h2>
          <kbd
            style={{
              padding: '2px 8px',
              fontSize: '11px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
              borderRadius: '4px',
              color: 'var(--theme-text-muted, #94a3b8)',
            }}
          >
            ESC to close
          </kbd>
        </div>

        {/* Search Combobox Input */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--theme-border, #1e293b)' }}>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded="true"
            aria-controls="command-palette-listbox"
            aria-activedescendant={
              filteredCommands.length > 0 ? `palette-item-${selectedIndex}` : undefined
            }
            placeholder="Type a command, navigation target, or search help runbooks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              backgroundColor: 'var(--theme-bg-input, #1e293b)',
              border: '1px solid var(--theme-border, #1e293b)',
              borderRadius: 'var(--theme-radius-md, 8px)',
              color: 'var(--theme-text-primary, #f8fafc)',
              fontSize: 'var(--theme-text-sm, 13px)',
              outline: 'none',
            }}
          />

          {/* Quick Action Chips Guide */}
          {!searchQuery && (
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingTop: '8px' }}>
              {[
                { label: '🏛️ Invoicing', query: 'invoicing' },
                { label: '👥 Payroll & HR', query: 'payroll' },
                { label: '📊 Node Health', query: 'health' },
                { label: '🏪 POS Terminal', query: 'pos' },
                { label: '📦 Inventory', query: 'inventory' },
                { label: '👑 Helper Leases', query: 'lease' },
              ].map((chip) => (
                <button
                  key={chip.label}
                  type="button"
                  onClick={() => setSearchQuery(chip.query)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid rgba(56, 189, 248, 0.25)',
                    background: 'rgba(56, 189, 248, 0.08)',
                    color: '#38bdf8',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results Listbox */}
        <div
          ref={listboxRef}
          id="command-palette-listbox"
          role="listbox"
          aria-label="Commands and help results"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px 12px',
            maxHeight: '400px',
          }}
        >
          {filteredCommands.length === 0 ? (
            <div
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: 'var(--theme-text-muted, #94a3b8)',
                fontSize: '13px',
              }}
            >
              No matching commands or help articles found for "{searchQuery}".
            </div>
          ) : (
            filteredCommands.map((cmd, idx) => {
              const isSelected = idx === selectedIndex;
              const categoryBadge = {
                action: { label: 'Action', color: '#10b981' },
                navigation: { label: 'Navigate', color: '#38bdf8' },
                help: { label: 'Help', color: '#f59e0b' },
                entity: { label: 'Entity', color: '#a855f7' },
              }[cmd.category];

              return (
                <div
                  key={cmd.id}
                  id={`palette-item-${idx}`}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => executeCommand(cmd)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: 'var(--theme-radius-md, 8px)',
                    backgroundColor: isSelected ? 'var(--theme-bg-card-hover, #1e293b)' : 'transparent',
                    border: isSelected ? '1px solid var(--theme-border-highlight, #38bdf8)' : '1px solid transparent',
                    cursor: 'pointer',
                    marginBottom: '4px',
                    transition: 'background-color 100ms ease',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          fontSize: '10px',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: '4px',
                          backgroundColor: `${categoryBadge.color}22`,
                          color: categoryBadge.color,
                          border: `1px solid ${categoryBadge.color}44`,
                        }}
                      >
                        {categoryBadge.label}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--theme-text-sm, 13px)',
                          fontWeight: isSelected ? 600 : 500,
                          color: isSelected
                            ? 'var(--theme-text-primary, #f8fafc)'
                            : 'var(--theme-text-secondary, #cbd5e1)',
                        }}
                      >
                        {cmd.title}
                      </span>
                    </div>
                    {cmd.subtitle && (
                      <span
                        style={{
                          fontSize: '11px',
                          color: 'var(--theme-text-muted, #94a3b8)',
                          paddingLeft: '56px',
                        }}
                      >
                        {cmd.subtitle}
                      </span>
                    )}
                  </div>

                  {cmd.shortcut && (
                    <kbd
                      style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        backgroundColor: 'var(--theme-bg-main, #0b0f19)',
                        border: '1px solid var(--theme-border, #1e293b)',
                        borderRadius: '4px',
                        color: 'var(--theme-text-muted, #94a3b8)',
                        fontFamily: 'var(--theme-font-mono)',
                      }}
                    >
                      {cmd.shortcut}
                    </kbd>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer info */}
        <div
          style={{
            padding: '10px 20px',
            backgroundColor: 'var(--theme-bg-subtle, #020617)',
            borderTop: '1px solid var(--theme-border, #1e293b)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: 'var(--theme-text-muted, #94a3b8)',
          }}
        >
          <span>Use ↑ and ↓ to navigate, Enter to select</span>
          <span>Press ? anytime for contextual help</span>
        </div>
      </div>
    </div>
  );
};
